import dayjs from 'dayjs';
import { UserpilotIdentifyProperties } from './userpilot.service';

export interface UserpilotOrg {
	group_id?: string | number | null;
	name: string;
	created_at: string;
	org_settings: { partner_guid: string };
}

interface UserpilotUser {
	user_id: string | number;
	created: string;
	provider_name?: string;
	auth_aliases?: { auth_provider?: { provider_name?: string } }[];
	is_root_user?: boolean;
	isInstructor?: boolean;
	isReviewerPlus?: boolean;
	isPresenter?: boolean;
}

interface DashboardUserpilotContext {
	surface: 'dashboard';
	user: UserpilotUser;
	org: UserpilotOrg | null | undefined;
	hasInstructorRole: boolean;
	hasReviewerRole: boolean;
}

interface LtiUserpilotContext {
	surface: 'lti';
	user: UserpilotUser;
	org: UserpilotOrg | null | undefined;
	lti: {
		lmsName?: string;
		version?: string;
		isCourseLevel: boolean;
		readonly: boolean;
		groupRole?: string;
	};
}

export type UserpilotIdentifyContext = DashboardUserpilotContext | LtiUserpilotContext;

const addDashboardProperties = (
	properties: UserpilotIdentifyProperties,
	context: DashboardUserpilotContext
): void => {
	Object.assign(properties, {
		provider_name: context.user.provider_name ?? context.user.auth_aliases?.[0]?.auth_provider?.provider_name,
		is_instructor: context.user.isInstructor === true || context.hasInstructorRole,
		is_reviewer_plus: context.user.isReviewerPlus === true || context.hasReviewerRole
	});
};

const addLtiProperties = (properties: UserpilotIdentifyProperties, context: LtiUserpilotContext): void => {
	Object.assign(properties, {
		provider_name: context.user.provider_name,
		is_instructor: context.user.isInstructor === true,
		is_reviewer_plus: context.user.isReviewerPlus === true,
		lms_name: context.lti.lmsName,
		lti_version: context.lti.version,
		lti_launch_type: context.lti.isCourseLevel ? 'course_level' : 'activity_level',
		lti_readonly: context.lti.readonly,
		group_role: context.lti.groupRole
	});
};

const getUserpilotOrgProperties = (org: UserpilotOrg | null | undefined): UserpilotIdentifyProperties => {
	if (org == null) {
		return {
			company: {
				name: 'No Org'
			}
		};
	}

	return {
		partner: org.org_settings.partner_guid,
		company: {
			id: org.group_id,
			name: org.name,
			created_at: org.created_at,
			partner: org.org_settings.partner_guid
		}
	};
};

export const buildUserpilotIdentifyProperties = (
	context: UserpilotIdentifyContext
): UserpilotIdentifyProperties => {
	const { user, org } = context;
	const properties: UserpilotIdentifyProperties = {
		user_id: String(user.user_id),
		app_surface: context.surface,
		is_root_user: user.is_root_user === true,
		is_presenter: user.isPresenter === true,
		created_at: dayjs(user.created).toISOString(),
		...getUserpilotOrgProperties(org)
	};

	if (context.surface === 'dashboard') {
		addDashboardProperties(properties, context);
	} else {
		addLtiProperties(properties, context);
	}

	return properties;
};
